import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, TestTube, Music, Heart, ArrowRight, Sparkles } from 'lucide-react'; // V1.1.1: Radio->Heart
import api from '@/services/api';

const GetStarted = ({ t }) => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasTests, setHasTests] = useState({ audiometric: false, tinnitus: false });

  useEffect(() => {
    loadUserProfile();
    checkExistingTests();
  }, []);

  const checkExistingTests = async () => {
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Check audiometric tests
      const audioResponse = await fetch(`${backendUrl}/api/audiometric-tests?user_id=${localStorage.getItem('user_id')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const audioData = await audioResponse.json();
      
      // Check tinnitus tests
      const tinnitusResponse = await fetch(`${backendUrl}/api/tinnitus-tests?user_id=${localStorage.getItem('user_id')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const tinnitusData = await tinnitusResponse.json();
      
      setHasTests({
        audiometric: audioData && audioData.length > 0,
        tinnitus: tinnitusData && tinnitusData.length > 0,
      });
      
      console.log('Tests status:', { audiometric: audioData?.length || 0, tinnitus: tinnitusData?.length || 0 });
    } catch (error) {
      console.error('Error checking tests:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendations = () => {
    if (!userProfile?.preferences) return [];

    const recommendations = [];
    const { primary_goal } = userProfile.preferences;
    const { distress_0_10 } = userProfile.tinnitus_profile || {};

    // Recommendation based on primary goal
    if (primary_goal === 'sleep') {
      recommendations.push({
        icon: Music,
        title: 'Explorez les sons Sommeil',
        desc: 'Découvrez notre collection de sons apaisants pour mieux dormir',
        action: () => navigate('/therapy'),
        color: 'indigo',
      });
    } else if (primary_goal === 'sos') {
      recommendations.push({
        icon: Heart, // V1.1.1: Radio->Heart
        title: 'Activez le module SOS',
        desc: 'Accès rapide à des exercices de respiration et sons calmants',
        action: () => navigate('/sos'),
        color: 'orange',
      });
    } else if (primary_goal === 'guided') {
      recommendations.push({
        icon: TestTube,
        title: 'Commencez par les tests',
        desc: 'Tests audiométrique et acouphènes pour une écoute personnalisée',
        action: () => navigate('/tests'),
        color: 'blue',
      });
    }

    // SOS recommendation if high distress
    if (distress_0_10 >= 7) {
      recommendations.unshift({
        icon: Heart, // V1.1.1: Radio->Heart
        title: 'SOS - Apaisement immédiat',
        desc: 'Votre niveau de gêne est élevé. Essayez notre module SOS maintenant.',
        action: () => navigate('/sos'),
        color: 'red',
        priority: true,
      });
    }

    return recommendations;
  };

  const steps = [
    {
      icon: TestTube,
      title: 'Passez les tests',
      desc: 'Tests audiométrique et acouphènes',
      action: () => navigate('/tests'),
      color: 'blue',
    },
    {
      icon: Music,
      title: 'Découvrez les paysages sonores',
      desc: 'Sons personnalisés selon votre profil',
      action: () => navigate('/therapy'),
      color: 'green',
    },
    // V1.1.1: Radio removed
  ];

  const recommendations = getRecommendations();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-purple-50 p-6 pb-32">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Success Header */}
        <div className="text-center space-y-4 pt-8">
          <div className="inline-block p-4 bg-gradient-to-br from-green-500 to-green-600 rounded-full shadow-lg">
            <CheckCircle size={48} className="text-white" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900">
            ✅ Profil enregistré !
          </h1>
          
          <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-green-200">
            <p className="text-base text-gray-700 leading-relaxed">
              Nous avons bien enregistré le résultat de cette personnalisation dans votre profil.
            </p>
            <p className="text-sm text-gray-600 mt-3">
              💡 Vous pourrez y revenir quand vous le souhaitez avec le bouton <strong>"Ma personnalisation"</strong> dans votre Profil.
            </p>
          </div>
        </div>

        {/* Privacy Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <p className="text-xs text-gray-700 leading-relaxed">
            🔒 <strong>Confidentialité</strong> : Vos réponses sont stockées de manière sécurisée 
            et utilisées uniquement pour personnaliser votre expérience. 
            Vous pouvez modifier votre personnalisation à tout moment depuis votre Profil.
          </p>
        </div>

        {/* Get Started Section */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900 text-center">
            🚀 Get Started
          </h2>
          
          {/* Test Recommendation Card */}
          {!hasTests.audiometric && !hasTests.tinnitus && (
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-3xl p-6 shadow-xl">
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <TestTube size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-2">
                    Première étape recommandée
                  </h3>
                  <p className="text-white/90 text-sm leading-relaxed">
                    Réalisez votre <strong>premier test audiométrique</strong> pour personnaliser 
                    précisément votre écoute.
                  </p>
                </div>
              </div>
              
              <div className="bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
                <p className="text-sm text-white/90 mb-3">
                  <strong>Pourquoi c'est important ?</strong>
                </p>
                <ul className="text-sm text-white/90 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-300 flex-shrink-0">✓</span>
                    <span>Thérapie adaptée à votre profil auditif unique</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-300 flex-shrink-0">✓</span>
                    <span>Identification précise de la fréquence de vos acouphènes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-300 flex-shrink-0">✓</span>
                    <span>Traitement audio personnalisé et optimisé</span>
                  </li>
                </ul>
                <p className="text-xs text-white/75 mt-3">
                  ⏱️ Durée : environ 10 minutes pour les deux tests
                </p>
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="space-y-3">
            {!hasTests.audiometric && !hasTests.tinnitus ? (
              <>
                <button
                  onClick={() => navigate('/tests')}
                  className="w-full py-5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <TestTube size={24} />
                  Commencer le test audiométrique
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-4 bg-white text-gray-700 font-semibold text-base rounded-2xl border-2 border-gray-300 hover:border-gray-400 transition-all active:scale-[0.98]"
                >
                  Plus tard → Accéder à l'accueil
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/')}
                className="w-full py-5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                Accéder à mon espace HEAREN
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetStarted;
